import "./DocumentGroupTagMenu.scss";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DOCUMENT_GROUP_TAG_LABELS,
  DOCUMENT_GROUP_TAG_TYPES,
  type DocumentGroupTag,
  type DocumentGroupTagType,
} from "services/document-group-tag-api";

const TAG_TYPE_ORDER = Object.values(DOCUMENT_GROUP_TAG_TYPES);
const MENU_GAP_PX = 4;
const MENU_ESTIMATED_HEIGHT_PX = 160;

type DocumentGroupTagMenuProps = {
  assignedTags: DocumentGroupTag[];
  enabledTypes: DocumentGroupTagType[];
  onAssign?: (type: DocumentGroupTagType) => void;
  onRemove?: (tag: DocumentGroupTag) => void;
  disabled?: boolean;
  readOnly?: boolean;
};

const DocumentGroupTagMenu = ({
  assignedTags,
  enabledTypes,
  onAssign,
  onRemove,
  disabled = false,
  readOnly = false,
}: DocumentGroupTagMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const assignedTagByType = new Map(assignedTags.map((tag) => [tag.type, tag]));
  const menuTypes = TAG_TYPE_ORDER.filter(
    (type) =>
      (!readOnly && enabledTypes.includes(type)) || assignedTagByType.has(type),
  );
  const assignedTypes = TAG_TYPE_ORDER.filter((type) =>
    assignedTagByType.has(type),
  );
  const assignedLabels = assignedTypes
    .map((type) => DOCUMENT_GROUP_TAG_LABELS[type])
    .join(", ");

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Read only menus only list labels, so focus stays on the trigger.
    menuRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]')
      ?.focus({ preventScroll: true });

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      }
    };
    const handleScroll = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const fitsBelow =
        rect.bottom + MENU_GAP_PX + MENU_ESTIMATED_HEIGHT_PX <=
        window.innerHeight;
      setMenuPosition({
        top: fitsBelow
          ? rect.bottom + MENU_GAP_PX
          : Math.max(
              MENU_GAP_PX,
              rect.top - MENU_GAP_PX - MENU_ESTIMATED_HEIGHT_PX,
            ),
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen((current) => !current);
  };

  const handleAssign = (type: DocumentGroupTagType) => {
    setIsOpen(false);
    onAssign?.(type);
  };

  const handleRemove = (tag: DocumentGroupTag) => {
    setIsOpen(false);
    onRemove?.(tag);
  };

  return (
    <>
      <button
        ref={triggerRef}
        className={`document-group-tag-trigger${
          assignedTypes.length > 0
            ? " document-group-tag-trigger--assigned"
            : ""
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
        aria-label="Document group tags"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={
          assignedLabels
            ? `Tags: ${assignedLabels}`
            : readOnly
              ? "No tags"
              : "Assign a tag"
        }
        disabled={disabled}
      >
        <span className="material-icons">sell</span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="document-group-tag-menu"
            role="menu"
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {menuTypes.map((type) => {
              const label = DOCUMENT_GROUP_TAG_LABELS[type];
              const assignedTag = assignedTagByType.get(type);

              if (assignedTag) {
                return (
                  <div
                    key={type}
                    className="document-group-tag-menu__item document-group-tag-menu__item--assigned"
                  >
                    <span className="document-group-tag-menu__label">
                      {label}
                    </span>
                    <span className="material-icons document-group-tag-menu__check">
                      check
                    </span>
                    {!readOnly && (
                      <button
                        className="document-group-tag-menu__remove"
                        role="menuitem"
                        aria-label={`Remove ${label} tag`}
                        onClick={() => handleRemove(assignedTag)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={type}
                  className="document-group-tag-menu__item"
                  role="menuitem"
                  aria-label={`Assign ${label} tag`}
                  onClick={() => handleAssign(type)}
                >
                  <span className="document-group-tag-menu__label">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
};

export default DocumentGroupTagMenu;
